import { isExactCodeMatch } from '../utils/exactCodeMatch';
import ApiWithLogging from '../utils/apiWithLogging';

// Carrier enum
// C9 (MANUAL_AUDIT_SYSTEM_FINDINGS): ตาราง SIM มีค่าย NT (National Telecom = CAT+TOT ควบรวม)
// แต่แพ็กเกจซิมไม่มี → ซิม NT สร้าง/หาแพ็กเกจของค่ายตัวเองไม่เจอ ต้องไปเลือก "อื่นๆ"
// ทำให้กรอง/จับคู่แพ็กเกจตามค่ายใช้ไม่ได้ · ต้องเรียงตรงกับ enum ฝั่ง backend
// (models/sim_package.py — MySQL เก็บ enum เป็น index ห้ามสลับลำดับ NT ต่อท้ายเสมอ)
export enum Carrier {
  TRUE = 'TRUE',
  AIS = 'AIS',
  DTAC = 'DTAC',
  OTHER = 'OTHER',
  NT = 'NT'
}

// Billing Cycle enum
export enum BillingCycle {
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY'
}

// ประเภทข้อมูลแพ็กเกจซิม
export interface SimPackage {
  id: number;
  code: string;
  carrier: Carrier;
  name: string;
  speed_mbps?: number;
  data_quota_gb?: number;  // NULL = Unlimited
  billing_cycle: BillingCycle;
  monthly_cost?: number;
  yearly_cost?: number;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

// ข้อมูลสำหรับการสร้างหรือแก้ไขแพ็กเกจซิม
export interface SimPackageForm {
  code: string;
  carrier: Carrier;
  name: string;
  speed_mbps?: number;
  data_quota_gb?: number;
  billing_cycle?: BillingCycle;
  monthly_cost?: number;
  yearly_cost?: number;
  is_active?: boolean;
}

// พารามิเตอร์สำหรับการดึงข้อมูลแพ็กเกจซิม
export interface GetSimPackagesParams {
  page?: number;
  limit?: number;
  search?: string;
  carrier?: Carrier;
  billing_cycle?: BillingCycle;
  is_active?: boolean;
  sort?: string;
  order?: 'asc' | 'desc';
}

// การตอบกลับจากการดึงข้อมูลแพ็กเกจซิม
export interface GetSimPackagesResponse {
  items: SimPackage[];
  total: number;
  page: number;
  limit: number;
}

// ข้อมูลสำหรับ dropdown
export interface SimPackageOption {
  id: number;
  code: string;
  carrier: Carrier;
  name: string;
  speed_mbps?: number;
  data_quota_gb?: number;
  billing_cycle: BillingCycle;
  monthly_cost?: number;
  yearly_cost?: number;
}

// Carrier labels (Thai)
export const CarrierLabels: Record<Carrier, string> = {
  [Carrier.TRUE]: 'ทรู',
  [Carrier.AIS]: 'เอไอเอส',
  [Carrier.DTAC]: 'ดีแทค',
  // NT อยู่ก่อน "อื่นๆ" เพื่อให้ dropdown เรียงค่ายจริงไว้ด้วยกัน แล้วปิดท้ายด้วยตัวเลือกรวม
  // (SimPackagePage สร้าง dropdown ทั้งฟอร์มและตัวกรองจาก map นี้โดยตรง)
  [Carrier.NT]: 'เอ็นที',
  [Carrier.OTHER]: 'อื่นๆ'
};

// Billing Cycle labels (Thai)
export const BillingCycleLabels: Record<BillingCycle, string> = {
  [BillingCycle.MONTHLY]: 'รายเดือน',
  [BillingCycle.YEARLY]: 'รายปี'
};

// บริการจัดการข้อมูลแพ็กเกจซิม
const simPackageService = {
  // ดึงรายการแพ็กเกจซิมทั้งหมด
  async getSimPackages(params: GetSimPackagesParams = {}): Promise<GetSimPackagesResponse> {
    try {
      const queryParams: any = {
        page: params.page || 1,
        limit: params.limit || 10
      };

      if (params.search !== undefined) queryParams.search = params.search;
      if (params.carrier !== undefined) queryParams.carrier = params.carrier;
      if (params.billing_cycle !== undefined) queryParams.billing_cycle = params.billing_cycle;
      if (params.is_active !== undefined) queryParams.is_active = params.is_active;
      if (params.sort !== undefined) queryParams.sort = params.sort;
      if (params.order !== undefined) queryParams.order = params.order;

      console.log('Fetching sim packages with params:', queryParams);

      const response = await ApiWithLogging.get<GetSimPackagesResponse>('/sim-packages/',
        { params: queryParams },
        {
          service: 'simPackageService',
          action: 'view_sim_packages_list',
          entityType: 'sim_package'
        }
      );

      console.log('Fetched sim packages response:', response);
      return response;
    } catch (error: any) {
      console.error('Error in getSimPackages service:', error);
      throw error;
    }
  },

  // ดึงรายการแพ็กเกจซิมที่ active สำหรับ dropdown
  async getActiveSimPackages(carrier?: Carrier): Promise<SimPackageOption[]> {
    try {
      const params: any = {};
      if (carrier) params.carrier = carrier;

      const response = await ApiWithLogging.get<SimPackageOption[]>('/sim-packages/active',
        { params },
        {
          service: 'simPackageService',
          action: 'view_active_sim_packages',
          entityType: 'sim_package'
        }
      );
      return response;
    } catch (error) {
      console.error('Error in getActiveSimPackages service:', error);
      throw error;
    }
  },

  // ดึงข้อมูลแพ็กเกจซิมตาม id
  async getSimPackage(id: number): Promise<SimPackage> {
    try {
      const response = await ApiWithLogging.get<SimPackage>(`/sim-packages/${id}`, {},
        {
          service: 'simPackageService',
          action: 'view_sim_package_detail',
          entityType: 'sim_package',
          entityId: id
        }
      );
      return response;
    } catch (error) {
      console.error('Error in getSimPackage service:', error);
      throw error;
    }
  },

  // สร้างแพ็กเกจซิมใหม่
  async createSimPackage(data: SimPackageForm): Promise<SimPackage> {
    try {
      const packageData = {
        code: data.code,
        carrier: data.carrier,
        name: data.name,
        speed_mbps: data.speed_mbps || null,
        data_quota_gb: data.data_quota_gb || null,
        billing_cycle: data.billing_cycle || BillingCycle.MONTHLY,
        monthly_cost: data.monthly_cost || null,
        yearly_cost: data.yearly_cost || null,
        is_active: data.is_active !== undefined ? data.is_active : true
      };

      console.log('Creating sim package with data:', packageData);

      const response = await ApiWithLogging.post<SimPackage>('/sim-packages/', packageData, {},
        {
          service: 'simPackageService',
          action: 'create_sim_package',
          entityType: 'sim_package',
          details: {
            code: data.code,
            carrier: data.carrier,
            name: data.name
          }
        }
      );
      return response;
    } catch (error: any) {
      console.error('Error in createSimPackage service:', error);
      throw error;
    }
  },

  // แก้ไขข้อมูลแพ็กเกจซิม
  // BUG-S162-P1: pass last-seen updated_at as `ifMatch` to enable backend optimistic lock (stale→409). Optional.
  async updateSimPackage(id: number, data: Partial<SimPackageForm>, ifMatch?: string): Promise<SimPackage> {
    try {
      console.log(`Updating sim package ID ${id} with data:`, data);

      const response = await ApiWithLogging.put<SimPackage>(`/sim-packages/${id}`, data, ifMatch ? { headers: { 'If-Match': ifMatch } } : {},
        {
          service: 'simPackageService',
          action: 'update_sim_package',
          entityType: 'sim_package',
          entityId: id,
          details: {
            name: data.name,
            carrier: data.carrier
          }
        }
      );
      return response;
    } catch (error: any) {
      console.error('Error in updateSimPackage service:', error);
      throw error;
    }
  },

  // ลบแพ็กเกจซิม
  async deleteSimPackage(id: number): Promise<SimPackage> {
    try {
      console.log(`Deleting sim package with ID: ${id}`);

      const response = await ApiWithLogging.delete<SimPackage>(`/sim-packages/${id}`, {},
        {
          service: 'simPackageService',
          action: 'delete_sim_package',
          entityType: 'sim_package',
          entityId: id
        }
      );
      return response;
    } catch (error) {
      console.error('Error in deleteSimPackage service:', error);
      throw error;
    }
  },

  // Toggle สถานะ active/inactive
  async toggleSimPackageStatus(id: number): Promise<SimPackage> {
    try {
      console.log(`Toggling sim package status for ID: ${id}`);

      const response = await ApiWithLogging.patch<SimPackage>(`/sim-packages/${id}/toggle`, {}, {},
        {
          service: 'simPackageService',
          action: 'toggle_sim_package_status',
          entityType: 'sim_package',
          entityId: id
        }
      );
      return response;
    } catch (error) {
      console.error('Error in toggleSimPackageStatus service:', error);
      throw error;
    }
  },

  // ตรวจสอบรหัสซ้ำ
  async checkCodeExists(code: string): Promise<boolean> {
    try {
      const response = await ApiWithLogging.get<{ items: SimPackage[]; total: number }>('/sim-packages/',
        { params: { search: code, limit: 100 } },
        {
          service: 'simPackageService',
          action: 'check_sim_package_code',
          entityType: 'sim_package',
          details: { code }
        }
      );
      // Check if any package has exact code match
      return isExactCodeMatch(response.items.map(pkg => pkg.code), code);
    } catch (error) {
      console.error('Error checking sim package code:', error);
      return false;
    }
  }
};

// Named exports สำหรับ React Query hooks
export const getSimPackages = simPackageService.getSimPackages;
export const getActiveSimPackages = simPackageService.getActiveSimPackages;
export const getSimPackage = simPackageService.getSimPackage;
export const createSimPackage = simPackageService.createSimPackage;
export const updateSimPackage = simPackageService.updateSimPackage;
export const deleteSimPackage = simPackageService.deleteSimPackage;
export const toggleSimPackageStatus = simPackageService.toggleSimPackageStatus;

export default simPackageService;
